import React, { FC, useEffect, useState } from 'react'
import { payslipFormType } from './PaySlipAdmin'
import { useForm } from 'react-hook-form'
import paySlipService from 'Services/paySlipService'
import Swal from 'sweetalert2'
import './CustomPayslipForm.scss'

const CustomPayslipForm: FC<{
  customPayslipUserId: string | undefined
  togglePayslipModal: () => void
}> = ({ customPayslipUserId, togglePayslipModal }) => {
  const [customRequest, setCustomRequest] = useState(false)
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear())

  const [selectedMonth, setSelectedMonth] = useState('')
  const [months, setMonths] = useState<{ name: string; value: number }[]>([])

  useEffect(() => {
    const today = new Date()
    const lastMonthIndex = today.getMonth() - 1

    if (selectedYear === today.getFullYear()) {
      // Only allow current and future months, plus last month if applicable
      const futureMonths = monthNames.filter((_, index) => index >= lastMonthIndex)
      setMonths(futureMonths)
    } else {
      // Allow all months for other years
      setMonths(monthNames)
    }
    // commenting this out to allow all months for other years
    setMonths(monthNames)
  }, [selectedYear])

  const monthNames = [
    { name: 'January', value: 1 },
    { name: 'February', value: 2 },
    { name: 'March', value: 3 },
    { name: 'April', value: 4 },
    { name: 'May', value: 5 },
    { name: 'June', value: 6 },
    { name: 'July', value: 7 },
    { name: 'August', value: 8 },
    { name: 'September', value: 9 },
    { name: 'October', value: 10 },
    { name: 'November', value: 11 },
    { name: 'December', value: 12 },
  ]

  const [earningCategories, setEarningCategories] = useState([
    { name: 'Basic Salary', active: false, amount: 0 },
    { name: 'Housing Allowance', active: false, amount: 0 },
    { name: 'Transport Allowance', active: false, amount: 0 },
    { name: 'Utilities', active: false, amount: 0 },
    { name: 'COLA', active: false, amount: 0 },
  ])
  //
  const [deductionCategories, setDeductionCategories] = useState([
    { name: 'PAYE Tax', active: false, amount: 0 },
    { name: 'Pension', active: false, amount: 0 },
    { name: 'Staff Loan', active: false, amount: 0 },
    { name: 'Other Deductions', active: false, amount: 0 },
  ])

  const [allowanceCategories, setAllowanceCategories] = useState([
    { name: 'Leave Allowance', active: false, amount: 0 },
    { name: 'Bonus', active: false, amount: 0 },
    { name: 'Commission', active: false, amount: 0 },
    { name: 'Expense Allowance', active: false, amount: 0 },
    { name: 'Others', active: false, amount: 0 },
  ])

  const [reliefCategories, setRentCategories] = useState([
    { name: 'Rent Relief', active: false, amount: 0 },
  ])

  const {
    handleSubmit: handleSubmit2,
    register: register2,
    formState: { errors: errors2 },
  } = useForm<payslipFormType>()

  const generateYears = (startYear: number, endYear: number) => {
    const years = []
    for (let year = startYear; year <= endYear; year++) {
      years.push(year)
    }
    return years
  }

  const currentYear = new Date().getFullYear()
  const newYears = generateYears(2020, currentYear)

  const sendCustomPayslip = async (data: payslipFormType) => {
    setCustomRequest(true)

    const earnings: any = earningCategories
      .filter((category) => category.active && category.amount !== 0)
      .map((category) => ({
        category: category.name,
        amount: category.amount,
      }))

    const deductions: any = deductionCategories
      .filter((category) => category.active && category.amount !== 0)
      .map((category) => ({
        category: category.name,
        amount: category.amount,
      }))

    const allowances: any = allowanceCategories
      .filter((category) => category.active && category.amount !== 0)
      .map((category) => ({
        category: category.name,
        amount: category.amount,
      }))

    const relief: any = reliefCategories
      .filter((category) => category.active && category.amount !== 0)
      .map((category) => ({
        category: category.name,
        amount: category.amount,
      }))

    Swal.fire({
      title: 'Loading...',
      text: 'Please wait while we process your request',
      allowOutsideClick: false,
      allowEscapeKey: false,
      allowEnterKey: false,
      didOpen: () => {
        Swal.showLoading()
      },
    })
    console.log({
      ...data,
      earnings,
      deductions,
      allowances,
      relief,
      userId: customPayslipUserId ?? '',
    })
    await paySlipService
      .sendCustomPayslipToUser({
        ...data,
        earnings,
        deductions,
        allowances,
        relief,
        userId: customPayslipUserId ?? data.userId,
      })
      .then(
        (res) => {
          Swal.close()
          setCustomRequest(false)
          togglePayslipModal()
          Swal.fire({
            icon: 'success',
            title: 'Completed',
            text: 'Your request has been sent successfully!',
          })
        },
        (error) => {
          setCustomRequest(false)
          Swal.close()
          console.log('productService error', error.response)
          const swalWithBootstrapButtons = Swal.mixin({
            customClass: {
              cancelButton: 'btn btn-danger',
            },
            buttonsStyling: false,
          })

          swalWithBootstrapButtons.fire({
            title: 'Oops!',
            text: error.response.data.error,
            icon: 'warning',
            showCancelButton: true,
            showConfirmButton: false,
            cancelButtonText: 'close',
            reverseButtons: true,
          })
        },
      )
  }

  const handleToggleCategory = (index: number) => {
    setEarningCategories((prevCategories) =>
      prevCategories.map((category, i) =>
        i === index ? { ...category, active: !category.active } : category,
      ),
    )
  }

  // Update the amount for the active earning category
  const handleEarningAmountChange = (index: number, value: number) => {
    setEarningCategories((prevCategories) =>
      prevCategories.map((category, i) =>
        i === index ? { ...category, amount: value } : category,
      ),
    )
  }

  const handleDeductionToggleCategory = (index: number) => {
    setDeductionCategories((prevCategories) =>
      prevCategories.map((category, i) =>
        i === index ? { ...category, active: !category.active } : category,
      ),
    )
  }

  // Update the amount for the active earning category
  const handleDeductionAmountChange = (index: number, value: number) => {
    setDeductionCategories((prevCategories) =>
      prevCategories.map((category, i) =>
        i === index ? { ...category, amount: value } : category,
      ),
    )
  }

  // allowance
  const handleAllowanceToggleCategory = (index: number) => {
    setAllowanceCategories((prevCategories) =>
      prevCategories.map((category, i) =>
        i === index ? { ...category, active: !category.active } : category,
      ),
    )
  }

  const handleAllowanceAmountChange = (index: number, value: number) => {
    setAllowanceCategories((prevCategories) =>
      prevCategories.map((category, i) =>
        i === index ? { ...category, amount: value } : category,
      ),
    )
  }

  // relief
  const handleRentToggleCategory = (index: number) => {
    setRentCategories((prevCategories) =>
      prevCategories.map((category, i) =>
        i === index ? { ...category, active: !category.active } : category,
      ),
    )
  }

  const handleRentAmountChange = (index: number, value: number) => {
    setRentCategories((prevCategories) =>
      prevCategories.map((category, i) =>
        i === index ? { ...category, amount: value } : category,
      ),
    )
  }

  return (
    <div className='custom-payslip-form'>
      <form onSubmit={handleSubmit2(sendCustomPayslip)}>
        <div className='row'>
          {!customPayslipUserId && (
            <div className='form-group mb-3 col'>
              <label htmlFor='userId'>User ID</label>
              <input type='text' id='userId' className='form-control' {...register2('userId')} />
              {errors2.userId && <small className='text-danger'>{errors2.userId.message}</small>}
            </div>
          )}

          <div className='form-group mb-3 col'>
            <label htmlFor='year-select'>Select Year:</label>
            <select
              id='year-select'
              className='form-control'
              value={selectedYear}
              {...register2('year', {
                required: 'Year is required',
                onChange: (e) => setSelectedYear(parseInt(e.target.value)),
              })}
            >
              {newYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
            {errors2.year && <small className='text-danger'>{errors2.year.message}</small>}
          </div>

          <div className='form-group mb-3 col'>
            <label htmlFor='month-select'>Select Month:</label>

            <select
              {...register2('month', {
                required: 'Month is required',
                onChange: (e) => setSelectedMonth(e.target.value),
              })}
              id='month-select'
              className='form-control'
              value={selectedMonth}
            >
              <option value='' disabled>
                -- Select Month --
              </option>
              {months.map((month) => (
                <option key={month.value} value={month.name}>
                  {month.name}
                </option>
              ))}
            </select>
            {errors2.month && <small className='text-danger'>{errors2.month.message}</small>}
          </div>
        </div>

        <div className='card-body row'>
          <div className='col-12 col-md-6 mt-3'>
            {earningCategories.map((response, index) => {
              return (
                response.active === true && (
                  <div className='form-group mt-3' key={index}>
                    <label htmlFor={`salaryEarningCategory-${index}`}>{response.name}</label>
                    <input
                      autoComplete='off'
                      type='number'
                      step='0.01'
                      id={`salaryEarningCategory-${index}`}
                      className='form-control'
                      defaultValue={response.amount}
                      onChange={(e) => handleEarningAmountChange(index, parseFloat(e.target.value))}
                      placeholder={`Enter ${response.name} amount`}
                    />
                  </div>
                )
              )
            })}
          </div>

          <div className='col-md-6'>
            <div className='card category-card'>
              <div className='card-header'>Select Earnings</div>
              <div className='card-body'>
                <ul>
                  {earningCategories.map((response, index) => {
                    return (
                      <li className='d-flex justify-content-between' key={index}>
                        <div>{response.name}</div>{' '}
                        <button
                          className='btn border-0'
                          type='button'
                          onClick={() => handleToggleCategory(index)}
                        >
                          {response.active ? '-' : '+'}
                        </button>
                      </li>
                    )
                  })}
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className='card-body row mt-4'>
          <div className='col-12 col-md-6 mt-3'>
            {allowanceCategories.map((response, index) => {
              return (
                response.active === true && (
                  <div className='form-group mt-3' key={index}>
                    <label htmlFor={`salaryEarningCategory-${index}`}>{response.name}</label>
                    <input
                      autoComplete='off'
                      type='number'
                      step='0.01'
                      id={`salaryEarningCategory-${index}`}
                      className='form-control'
                      defaultValue={response.amount}
                      onChange={(e) =>
                        handleAllowanceAmountChange(index, parseFloat(e.target.value))
                      }
                      placeholder={`Enter ${response.name} amount`}
                    />
                  </div>
                )
              )
            })}
          </div>

          <div className='col-md-6'>
            <div className='card category-card'>
              <div className='card-header'>Select Allowances</div>
              <div className='card-body'>
                <ul>
                  {allowanceCategories.map((response, index) => {
                    return (
                      <li className='d-flex justify-content-between' key={index}>
                        <div>{response.name}</div>{' '}
                        <button
                          className='btn border-0'
                          type='button'
                          onClick={() => handleAllowanceToggleCategory(index)}
                        >
                          {response.active ? '-' : '+'}
                        </button>
                      </li>
                    )
                  })}
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className='card-body row mt-4'>
          <div className='col-12 col-md-6 mt-3'>
            {reliefCategories.map((response, index) => {
              return (
                response.active === true && (
                  <div className='form-group mt-3' key={index}>
                    <label htmlFor={`salaryEarningCategory-${index}`}>{response.name}</label>
                    <input
                      autoComplete='off'
                      type='number'
                      step='0.01'
                      id={`salaryEarningCategory-${index}`}
                      className='form-control'
                      defaultValue={response.amount}
                      onChange={(e) => handleRentAmountChange(index, parseFloat(e.target.value))}
                      placeholder={`Enter ${response.name} amount`}
                    />
                  </div>
                )
              )
            })}
          </div>

          <div className='col-md-6'>
            <div className='card category-card relief'>
              <div className='card-header'>Select Reliefs</div>
              <div className='card-body'>
                <ul>
                  {reliefCategories.map((response, index) => {
                    return (
                      <li className='d-flex justify-content-between' key={index}>
                        <div>{response.name}</div>{' '}
                        <button
                          className='btn border-0'
                          type='button'
                          onClick={() => handleRentToggleCategory(index)}
                        >
                          {response.active ? '-' : '+'}
                        </button>
                      </li>
                    )
                  })}
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className='card-body row mt-2'>
          <div className='col-12 col-md-6 mt-3'>
            {deductionCategories.map((response, index) => {
              return (
                response.active === true && (
                  <div className='form-group mt-3' key={index}>
                    <label htmlFor={`salaryEarningCategory-${index}`}>{response.name}</label>
                    <input
                      autoComplete='off'
                      type='number'
                      step='0.01'
                      id={`salaryEarningCategory-${index}`}
                      className='form-control'
                      defaultValue={response.amount}
                      onChange={(e) =>
                        handleDeductionAmountChange(index, parseFloat(e.target.value))
                      }
                      placeholder={`Enter ${response.name} amount`}
                    />
                  </div>
                )
              )
            })}
          </div>

          <div className='col-md-6'>
            <div className='card category-card'>
              <div className='card-header'>Select Deductions</div>
              <div className='card-body'>
                <ul>
                  {deductionCategories.map((response, index) => {
                    return (
                      <li className='d-flex justify-content-between' key={index}>
                        <div>{response.name}</div>{' '}
                        <button
                          className='btn border-0'
                          type='button'
                          onClick={() => handleDeductionToggleCategory(index)}
                        >
                          {response.active ? '-' : '+'}
                        </button>
                      </li>
                    )
                  })}
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div className='form-group mb-3 mt-5'>
          <button type='submit' className='btn btn-default btn-0' disabled={customRequest}>
            Send {customRequest && <span className='spinner-border spinner-border-sm'></span>}
          </button>
        </div>
      </form>
    </div>
  )
}

export default CustomPayslipForm
